/**
 * YearPicker.jsx
 *
 * Replaces the cramped <select> year filter in the Topbar with a compact
 * button that opens a rich popover:
 *
 *   - Big "All Time" toggle at the top
 *   - Grid of individual year chips (single-click switch)
 *   - "Compare" mode: same chips become multi-selectable
 *   - Quick presets: "Last 2 / 3 / 4 years"
 *   - Apply / Reset
 *
 * Selected value stored in AppContext exactly the same way as before:
 *   - 'all'           — All Time
 *   - <number>        — single year
 *   - [<y1>, <y2>...] — multi-year (sorted)
 */
import { useState, useMemo, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useApp } from '../../context/AppContext';
import { Icon } from '../ui/Icon';

const YEARS = [2026, 2025, 2024, 2023, 2022, 2021];
const PRESETS = [
  { label: 'Last 2 yr', years: [2025, 2026] },
  { label: 'Last 3 yr', years: [2024, 2025, 2026] },
  { label: 'Last 4 yr', years: [2023, 2024, 2025, 2026] },
];

function summarise(v) {
  if (v === 'all') return 'All Time';
  if (Array.isArray(v)) {
    if (v.length === 0) return 'All Time';
    if (v.length === 1) return String(v[0]);
    if (v.length <= 3) return v.join(' + ');
    return `${v[0]}–${v[v.length - 1]} (${v.length} yrs)`;
  }
  return String(v);
}

export function YearPicker() {
  const { selectedYear, setSelectedYear } = useApp();
  const [open, setOpen] = useState(false);
  const [compareMode, setCompareMode] = useState(Array.isArray(selectedYear));
  const [draft, setDraft] = useState(() => normalize(selectedYear));
  const btnRef = useRef(null);
  const popRef = useRef(null);
  const [coords, setCoords] = useState({ top: 0, right: 0 });

  // Reset draft when popover opens / context changes.
  useEffect(() => {
    if (open) {
      setDraft(normalize(selectedYear));
      setCompareMode(Array.isArray(selectedYear));
    }
  }, [open, selectedYear]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (popRef.current?.contains(e.target)) return;
      if (btnRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const toggleOpen = () => {
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setCoords({ top: r.bottom + 6, right: window.innerWidth - r.right });
    }
    setOpen((v) => !v);
  };

  const apply = () => {
    if (draft === 'all') setSelectedYear('all');
    else if (Array.isArray(draft)) {
      if (draft.length === 0) setSelectedYear('all');
      else if (draft.length === 1) setSelectedYear(draft[0]);
      else setSelectedYear([...draft].sort());
    } else setSelectedYear(draft);
    setOpen(false);
  };

  const reset = () => {
    setDraft('all');
    setCompareMode(false);
  };

  const pickSingle = (y) => {
    if (compareMode) {
      // Toggle in multi-select
      const cur = Array.isArray(draft) ? draft : draft === 'all' ? [] : [draft];
      const next = cur.includes(y) ? cur.filter((x) => x !== y) : [...cur, y];
      setDraft(next.length === 0 ? 'all' : next);
    } else {
      setDraft(y);
    }
  };

  const isActive = (y) => {
    if (draft === 'all') return false;
    if (Array.isArray(draft)) return draft.includes(y);
    return draft === y;
  };

  const label = useMemo(() => summarise(selectedYear), [selectedYear]);
  const isMulti = Array.isArray(selectedYear);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={toggleOpen}
        className="flex items-center gap-2 px-3.5 py-2 bg-white border border-border rounded-md text-[13px] font-semibold text-text-dark hover:border-gat-green focus:outline-none focus:ring-2 focus:ring-gat-green/30 transition-colors min-w-[170px] justify-between"
        title="Filter by year"
      >
        <span className="flex items-center gap-2">
          <Icon name="filter" size={13} color={isMulti ? '#1B5E20' : '#666'} />
          <span className={isMulti ? 'text-gat-greenDarkest' : 'text-text-dark'}>{label}</span>
        </span>
        <span className={`text-[10px] ${open ? 'rotate-180' : ''} transition-transform text-text-muted`}>▼</span>
      </button>

      {open &&
        createPortal(
          <div
            ref={popRef}
            className="fixed z-[1000] bg-white border border-border rounded-lg shadow-2xl w-[340px] text-text-dark"
            style={{ top: coords.top, right: coords.right }}
          >
            {/* All-time toggle */}
            <div className="p-3 border-b border-border">
              <button
                type="button"
                onClick={() => {
                  setDraft('all');
                  setCompareMode(false);
                }}
                className={[
                  'w-full flex items-center justify-between px-3 py-2 rounded-md font-bold text-[12.5px] transition-all',
                  draft === 'all' ? 'bg-gat-greenDarkest text-white' : 'bg-surface-2 text-text-dark hover:bg-gat-green/10',
                ].join(' ')}
              >
                <span className="flex items-center gap-2">
                  <Icon name="clock" size={14} color={draft === 'all' ? '#fff' : '#1B5E20'} />
                  All Time
                </span>
                <span className="text-[10.5px] font-mono opacity-80">2012–present</span>
              </button>
            </div>

            {/* Compare-mode toggle */}
            <div className="px-3 pt-2 pb-1 flex items-center justify-between">
              <span className="text-[10px] uppercase font-bold tracking-[1.4px] text-text-muted">Pick a year</span>
              <button
                type="button"
                onClick={() => setCompareMode((v) => !v)}
                className={[
                  'text-[10.5px] font-bold px-2 py-0.5 rounded',
                  compareMode ? 'bg-gat-green text-white' : 'bg-surface-2 text-text-body hover:bg-gat-green/10',
                ].join(' ')}
              >
                {compareMode ? '✓ Multi-select' : 'Select years'}
              </button>
            </div>

            {/* Year grid */}
            <div className="px-3 pb-2 grid grid-cols-3 gap-1.5">
              {YEARS.map((y) => (
                <button
                  key={y}
                  type="button"
                  onClick={() => pickSingle(y)}
                  className={[
                    'px-2 py-2 rounded-md font-bold text-[13px] transition-all border-2',
                    isActive(y)
                      ? 'bg-gat-green text-white border-gat-greenDarkest shadow-sm'
                      : 'bg-white text-text-dark border-border hover:border-gat-green hover:bg-gat-green/5',
                  ].join(' ')}
                >
                  {y}
                  {y === 2026 && (
                    <div className={`text-[8.5px] font-semibold tracking-[1px] mt-0.5 ${isActive(y) ? 'opacity-80' : 'text-text-muted'}`}>
                      CURRENT
                    </div>
                  )}
                </button>
              ))}
            </div>

            {/* Quick presets */}
            <div className="px-3 pb-2 border-t border-border pt-2">
              <div className="text-[10px] uppercase font-bold tracking-[1.4px] text-text-muted mb-1.5">Quick presets</div>
              <div className="flex gap-1.5">
                {PRESETS.map((p) => (
                  <button
                    key={p.label}
                    type="button"
                    onClick={() => {
                      setDraft(p.years);
                      setCompareMode(true);
                    }}
                    className="flex-1 px-2 py-1.5 bg-surface-2 hover:bg-gat-green/10 rounded text-[10.5px] font-semibold text-text-dark transition-colors"
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-between items-center bg-surface-2 px-3 py-2 border-t border-border">
              <button type="button" onClick={reset} className="text-[11px] font-semibold text-text-body hover:text-text-dark">
                Reset
              </button>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="px-3 py-1 text-[11px] font-semibold text-text-body hover:bg-white rounded"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={apply}
                  className="px-3 py-1 text-[11px] font-bold bg-gat-green text-white rounded hover:bg-gat-greenLight"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}

function normalize(v) {
  if (v == null || v === 'all') return 'all';
  if (Array.isArray(v)) return v.length ? [...v] : 'all';
  return v;
}
